import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { MapPin } from "lucide-react";
import { FaSpinner, FaPen, FaCheck } from "react-icons/fa";
import toast from "react-hot-toast";
import { store } from "../../../Store/store";
import * as PartnerService from "../../../Services/Partner/PartnerService";

const ADDRESS_MAX_LENGTH = 200;

const MyClinicAddressSection = ({ primaryBookingId, locationOptions, isResolvingLocation }) => {
    const { register, setValue, watch, formState: { errors } } = useFormContext();
    const userInfo = store.getState();
    const pincode = watch("pincode") || "";
    const accountDetails = userInfo?.Partner_Account_Details;
    const addressFromCRM = accountDetails?.MHL_address__c;
    const addressFromPP = userInfo?.userData?.Address;
    const isAddressUpdated = userInfo?.userData?.IsAddressUpdated;

    const addressLine1 = watch("addressLine1") || "";
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isSavingAddress, setIsSavingAddress] = useState(false);
    const [addressBeforeEdit, setAddressBeforeEdit] = useState("");

    useEffect(() => {
        const effectiveAddress = (addressFromPP || addressFromCRM || "").trim();
        if (!effectiveAddress) return;
        setValue("addressLine1", effectiveAddress, { shouldValidate: true });
        setValue("addressLine", effectiveAddress, { shouldValidate: true });
    }, [addressFromPP, addressFromCRM]);

    const handleToggleAddressEdit = () => {
        if (!isEditingAddress) {
            setAddressBeforeEdit(addressLine1);
            setIsEditingAddress(true);
            setValue("addressEditing", true, { shouldValidate: true });
            return;
        }

        setIsSavingAddress(true);

        const trimmedAddress = addressLine1.trim();

        const payload = {
            Address: trimmedAddress,
            PartnerCode: userInfo?.userData?.Parent_Partner_Code,
        };

        PartnerService.UpdatePartnerAddress(payload)
            .then(({ data }) => {
                if (!data?.IsSuccess) {
                    toast.error(data?.ResponseMessage || "Failed to update address.");
                    return;
                }
                setValue("addressLine1", trimmedAddress, { shouldValidate: true });
                setValue("addressLine", trimmedAddress, { shouldValidate: true });
                store.dispatch({ type: "update-user-data", payload: { Address: trimmedAddress, IsAddressUpdated: true } });
                toast.success("Address updated successfully.");
                setIsEditingAddress(false);
                setValue("addressEditing", false, { shouldValidate: true });
            })
            .catch(() => toast.error("Something went wrong while updating the address."))
            .finally(() => setIsSavingAddress(false));
    };

    const handleCancelAddressEdit = () => {
        setValue("addressLine1", addressBeforeEdit, { shouldValidate: true });
        setValue("addressLine", addressBeforeEdit, { shouldValidate: true });
        setIsEditingAddress(false);
        setValue("addressEditing", false, { shouldValidate: true });
    };

    return (
        <div className="hvt-quick-section">
            <div className="hvt-quick-section-title"><MapPin size={13} /> Location</div>

            <div className="hvt-field">
                <label className="required">Pincode</label>
                <div className="position-relative">
                    <input type="text" className="form-control" value={pincode || ""} disabled readOnly />
                    <input type="hidden" {...register("pincode", { required: "Required" })} />
                    {isResolvingLocation && <FaSpinner className="fa-spin position-absolute" style={{ right: 10, top: 12 }} />}
                </div>
                {errors.pincode && <small className="text-danger">{errors.pincode.message}</small>}
            </div>

            <div className="hvt-field mt-2">
                <label className="required">Location</label>
                {primaryBookingId ? (
                    <>
                        <input type="text" className="form-control" value={watch("locationName") || ""} disabled readOnly />
                        <input type="hidden" {...register("locationId", { required: "Required" })} />
                    </>
                ) : (
                    <select
                        className="form-select"
                        {...register("locationId", {
                            required: "Required",
                            onChange: (e) => {
                                const selected = locationOptions.find((l) => String(l.Locationid) === e.target.value) || null;
                                setValue("locationName", selected?.Location || "");
                            },
                        })}
                    >
                        <option value="">
                            {isResolvingLocation ? "Resolving..." : locationOptions.length ? "Select Location" : "No location found for this pincode"}
                        </option>
                        {locationOptions.map((loc) => (
                            <option key={loc.Locationid} value={loc.Locationid}>{loc.Location}</option>
                        ))}
                    </select>
                )}
                {errors.locationId && <small className="text-danger">{errors.locationId.message}</small>}
            </div>

            <div className="hvt-field mt-2">
                <div className="d-flex justify-content-between align-items-center">
                    <label className="required mb-0">Address</label>
                    <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">{addressLine1.length}/{ADDRESS_MAX_LENGTH}</small>
                        {!primaryBookingId && !isAddressUpdated && isEditingAddress && (
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                style={{ fontSize: 12.5, textDecoration: "none", color: "#6c757d" }}
                                onClick={handleCancelAddressEdit}
                                disabled={isSavingAddress}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
                <div className="position-relative">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Address"
                        maxLength={ADDRESS_MAX_LENGTH}
                        style={!primaryBookingId && !isAddressUpdated ? { paddingRight: 34 } : undefined}
                        disabled={!!primaryBookingId || isAddressUpdated || !isEditingAddress || isSavingAddress}
                        {...register("addressLine1", {
                            required: "Required",
                            maxLength: { value: ADDRESS_MAX_LENGTH, message: `Max ${ADDRESS_MAX_LENGTH} characters` },
                            onChange: (e) => {
                                setValue("addressLine1", e.target.value, { shouldValidate: true });
                                setValue("addressLine", e.target.value, { shouldValidate: true });
                            },
                        })}
                    />
                    {!primaryBookingId && !isAddressUpdated && (
                        <button
                            type="button"
                            className="btn p-0 d-flex align-items-center justify-content-center position-absolute"
                            style={{ right: 10, top: "50%", transform: "translateY(-50%)", width: 20, height: 20, border: "none", background: "transparent", color: "var(--color-primary)" }}
                            onClick={handleToggleAddressEdit}
                            disabled={isSavingAddress}
                            title={isEditingAddress ? "Save address" : "Edit address"}
                        >
                            {isSavingAddress ? <FaSpinner className="fa-spin" size={13} /> : isEditingAddress ? <FaCheck size={13} /> : <FaPen size={13} />}
                        </button>
                    )}
                </div>
                {errors.addressLine1 && <small className="text-danger">{errors.addressLine1.message}</small>}
                {errors.addressEditing && <small className="text-danger d-block">{errors.addressEditing.message}</small>}
                {primaryBookingId && <small className="text-muted">Locked — additional patients use the same address.</small>}
                {!primaryBookingId && isAddressUpdated && <small className="text-muted">This address has already been updated once and cannot be changed again.</small>}
            </div>

            <input type="hidden" {...register("addressLine", { required: "Required" })} />
            <input type="hidden" {...register("addressEditing", { validate: (v) => !v || "Please save your address changes (tap the checkmark) before continuing." })} />
        </div>
    );
};

export default MyClinicAddressSection;
